import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Select, Input, Switch, message, Row, Col, Tooltip, Alert } from 'antd';
import request from '../../../utils/request';

const { Option } = Select;

interface PolicyItem {
  id?: number;
  resource_key: string;
  field_key: string;
  role: string;
  sub_role?: string | null;
  is_visible_read: boolean;
  is_visible_export: boolean;
}

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理员' },
  { value: 'hq_staff', label: '总部员工' },
  { value: 'warehouse_supervisor', label: '仓库主管' },
  { value: 'warehouse_staff', label: '仓库员工' },
  { value: 'customer', label: '客户' },
];

const SUB_ROLE_OPTIONS = [
  { value: 'main_account', label: '主账号' },
  { value: 'sub_account', label: '子账号' },
];

const SensitiveFieldsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<PolicyItem[]>([]);
  const [resourceKey, setResourceKey] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [subRole, setSubRole] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState<boolean>(false);
  const [newItem, setNewItem] = useState<PolicyItem>({
    resource_key: '',
    field_key: '',
    role: 'warehouse_staff',
    sub_role: undefined,
    is_visible_read: false,
    is_visible_export: false,
  });

  const fetchList = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (resourceKey) params.resource_key = resourceKey;
      if (role) params.role = role; // 为空表示不过滤角色
      if (subRole !== undefined) params.sub_role = subRole || '';
      const resp = await request.get('/api/v1/permission-management/sensitive-field-policies/', { params });
      setList(resp.data?.results || resp.data || []);
    } catch (e) {
      message.error('加载敏感字段策略失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [resourceKey, role, subRole]);

  const handleCreate = async () => {
    try {
      setCreating(true);
      const payload = { ...newItem } as any;
      if (!payload.resource_key || !payload.field_key || !payload.role) {
        message.warning('请完整填写资源、字段与角色');
        return;
      }
      const resp = await request.post('/api/v1/permission-management/sensitive-field-policies/', payload);
      if (resp.status === 201) {
        message.success('创建成功');
        setNewItem({ resource_key: '', field_key: '', role, sub_role: subRole, is_visible_read: false, is_visible_export: false });
        fetchList();
      }
    } catch (e: any) {
      const data = e?.response?.data;
      const nonField = Array.isArray(data?.non_field_errors) ? data.non_field_errors.join('; ') : undefined;
      const fieldMsg = typeof data === 'object' && data ? Object.entries(data)
        .filter(([k]) => k !== 'non_field_errors')
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`)
        .join('; ') : undefined;
      message.error(nonField || fieldMsg || data?.detail || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (record: PolicyItem, patch: Partial<PolicyItem>) => {
    try {
      const resp = await request.put(`/api/v1/permission-management/sensitive-field-policies/${record.id}/`, { ...record, ...patch });
      if (resp.status === 200) {
        message.success('已更新');
        fetchList();
      }
    } catch (e: any) {
      const data = e?.response?.data;
      const nonField = Array.isArray(data?.non_field_errors) ? data.non_field_errors.join('; ') : undefined;
      const fieldMsg = typeof data === 'object' && data ? Object.entries(data)
        .filter(([k]) => k !== 'non_field_errors')
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`)
        .join('; ') : undefined;
      message.error(nonField || fieldMsg || data?.detail || '更新失败');
    }
  };

  const handleDelete = async (record: PolicyItem) => {
    try {
      await request.delete(`/api/v1/permission-management/sensitive-field-policies/${record.id}/`);
      message.success('已删除');
      fetchList();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  return (
    <div className="p-4">
      <Card title="敏感字段管理" bordered>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="使用说明"
          description={
            <div style={{ lineHeight: 1.6 }}>
              <div>1) 通过上方筛选选择“资源键/角色/子角色”，查看已配置的字段可见性。</div>
              <div>2) 在“新建策略”中填写资源键与字段键（如 customer__username），并设置“读/导出”可见性后创建。</div>
              <div>3) 列表中的开关可直接修改可见性；删除可移除此策略，恢复默认可见性。</div>
              <div>4) 生效路径：后端会在用户权限接口下发 hidden_fields_by_resource，序列化器按该清单脱敏；前端也会据此隐藏列与筛选。</div>
            </div>
          }
        />
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col>
            <Input placeholder="资源键，如 project.Project" value={resourceKey} onChange={(e) => setResourceKey(e.target.value)} style={{ width: 240 }} allowClear />
          </Col>
          <Col>
            <Select
              value={role || undefined}
              onChange={setRole as any}
              style={{ width: 180 }}
              allowClear
              placeholder="角色(可选)"
            >
              {ROLE_OPTIONS.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select>
          </Col>
          <Col>
            <Select value={subRole} onChange={setSubRole} style={{ width: 180 }} allowClear placeholder="子角色(可选)">
              {SUB_ROLE_OPTIONS.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
            </Select>
          </Col>
          <Col>
            <Button onClick={fetchList}>刷新</Button>
          </Col>
        </Row>

        <Card size="small" style={{ marginBottom: 12 }} title="新建策略">
          <Space wrap>
            <Input placeholder="资源键" value={newItem.resource_key} onChange={(e) => setNewItem(i => ({ ...i, resource_key: e.target.value }))} style={{ width: 240 }} />
            <Input placeholder="字段键" value={newItem.field_key} onChange={(e) => setNewItem(i => ({ ...i, field_key: e.target.value }))} style={{ width: 240 }} />
            <Select value={newItem.role} onChange={(v) => setNewItem(i => ({ ...i, role: v }))} style={{ width: 180 }}>
              {ROLE_OPTIONS.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select>
            <Select value={newItem.sub_role as any} onChange={(v) => setNewItem(i => ({ ...i, sub_role: v }))} style={{ width: 180 }} allowClear placeholder="子角色(可选)">
              {SUB_ROLE_OPTIONS.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
            </Select>
            <Tooltip title="读取是否可见">
              <span>
                可见(读)
                <Switch checked={newItem.is_visible_read} onChange={(v) => setNewItem(i => ({ ...i, is_visible_read: v }))} style={{ marginLeft: 6 }} />
              </span>
            </Tooltip>
            <Tooltip title="导出是否可见">
              <span>
                可见(导出)
                <Switch checked={newItem.is_visible_export} onChange={(v) => setNewItem(i => ({ ...i, is_visible_export: v }))} style={{ marginLeft: 6 }} />
              </span>
            </Tooltip>
            <Button type="primary" loading={creating} onClick={handleCreate}>创建</Button>
          </Space>
        </Card>

        <Table
          rowKey={(r) => `${r.id}`}
          loading={loading}
          dataSource={list}
          columns={[
            { title: '资源键', dataIndex: 'resource_key' },
            { title: '字段键', dataIndex: 'field_key' },
            { title: '角色', dataIndex: 'role' },
            { title: '子角色', dataIndex: 'sub_role' },
            { title: '可见(读)', dataIndex: 'is_visible_read', render: (v: boolean, r: PolicyItem) => <Switch checked={v} onChange={(val) => handleUpdate(r, { is_visible_read: val })} /> },
            { title: '可见(导出)', dataIndex: 'is_visible_export', render: (v: boolean, r: PolicyItem) => <Switch checked={v} onChange={(val) => handleUpdate(r, { is_visible_export: val })} /> },
            { title: '操作', key: 'action', render: (_: any, r: PolicyItem) => <Button danger onClick={() => handleDelete(r)}>删除</Button> },
          ]}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default SensitiveFieldsPage;


